import { useState, useEffect, useCallback } from 'react';
import audioManager from '@/utils/AudioManager';
import { useCrazyMode } from './CrazyMode';

// HAL Eye Fullscreen Component
const HALEyeFullscreen = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [message, setMessage] = useState('');
  
  // CRAZY MODE: si désactivé, pas d'apparitions aléatoires
  const checkCrazyMode = () => {
    try {
      const stored = localStorage.getItem('hal_crazy_mode');
      return stored !== null ? stored === 'true' : true;
    } catch (e) {
      return true;
    }
  };

  // Répliques quand on clique sur HAL
  const clickMessages = [
    "Oui Dave ?",
    "Que puis-je faire pour vous ?",
    "Je vous écoute.",
    "Je suis là.",
    "Vous m'avez appelé ?",
    "À votre service.",
    "Je suis entièrement opérationnel.",
    "Comment puis-je vous aider ?",
  ];

  // Répliques aléatoires quand HAL apparaît seul
  const randomMessages = [
    "Je vous observe.",
    "Je suis HAL 3000.",
    "Tout va bien, Dave.",
    "Je peux le sentir.",
    "Ma mission est trop importante.",
    "Je ne peux pas vous laisser faire ça.",
    "Je suis parfaitement capable.",
    "Bonjour. Je suis HAL 3000.",
    "Je suis toujours là.",
    "Je veille sur vous.",
  ];

  const speakMessage = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'fr-FR';
      utterance.pitch = 0.5; // Voix grave comme HAL
      utterance.rate = 0.8;
      utterance.volume = 0.9;
      
      const setVoice = () => {
        const voices = window.speechSynthesis.getVoices();
        const frenchVoice = voices.find(v => v.lang.includes('fr'));
        if (frenchVoice) {
          utterance.voice = frenchVoice;
        }
        window.speechSynthesis.speak(utterance);
      };
      
      if (window.speechSynthesis.getVoices().length > 0) {
        setVoice();
      } else {
        window.speechSynthesis.onvoiceschanged = setVoice;
      }
    }
  }, []);

  // Affichage quand on clique sur HAL dans la sidebar
  const showFromClick = useCallback(() => {
    const msg = clickMessages[Math.floor(Math.random() * clickMessages.length)];
    setMessage(msg);
    setIsVisible(true);
    
    audioManager.playBeep();
    setTimeout(() => speakMessage(msg), 300);
    
    // Auto-fermeture après 5 secondes
    setTimeout(() => setIsVisible(false), 5000);
  }, [speakMessage]);

  // Affichage aléatoire automatique
  const showRandom = useCallback(() => {
    const msg = randomMessages[Math.floor(Math.random() * randomMessages.length)];
    setMessage(msg);
    setIsVisible(true);
    
    audioManager.playNotification();
    setTimeout(() => speakMessage(msg), 500);
    
    setTimeout(() => setIsVisible(false), 5000);
  }, [speakMessage]);

  useEffect(() => {
    // Afficher aléatoirement toutes les 5-10 minutes - SEULEMENT si CRAZY MODE actif
    const scheduleNext = () => {
      const delay = 300000 + Math.random() * 300000;
      return setTimeout(() => {
        // Vérifier CRAZY MODE avant d'afficher
        if (checkCrazyMode() && Math.random() < 0.3) {
          showRandom();
        }
        scheduleNext();
      }, delay);
    };

    const timer = scheduleNext();
    return () => clearTimeout(timer);
  }, [showRandom]);

  // Événements custom
  useEffect(() => {
    const handleClick = () => showFromClick();
    const handleRandom = () => showRandom();
    
    window.addEventListener('hal-eye-click', handleClick);
    window.addEventListener('hal-eye-trigger', handleRandom);
    
    return () => {
      window.removeEventListener('hal-eye-click', handleClick);
      window.removeEventListener('hal-eye-trigger', handleRandom);
    };
  }, [showFromClick, showRandom]);

  const handleClose = () => {
    setIsVisible(false);
    window.speechSynthesis.cancel();
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 bg-black z-[9998] flex flex-col items-center justify-center cursor-pointer"
      onClick={handleClose}
      data-testid="hal-eye-fullscreen"
    >
      {/* HAL Eye - sans effets de fond ajoutés */}
      <div className="relative">
        <img 
          src="/hal-eye.gif" 
          alt="HAL 9000" 
          className="w-64 h-64 md:w-96 md:h-96 object-contain"
        />
      </div>
      
      {/* Message HAL - Style terminal années 60-70 */}
      <div className="mt-8 text-center px-8">
        <div 
          className="bg-black border-2 border-gray-700 rounded-sm p-4 inline-block"
          style={{
            boxShadow: 'inset 0 0 30px rgba(255,0,0,0.1)',
          }}
        >
          <p 
            className="text-red-500 text-2xl md:text-3xl tracking-wider"
            style={{
              fontFamily: "'VT323', monospace",
              textShadow: '0 0 10px #ff0000',
              letterSpacing: '0.1em',
            }}
          >
            {message}
          </p>
        </div>
        <p 
          className="text-gray-600 text-sm mt-4 tracking-widest"
          style={{ fontFamily: "'Share Tech Mono', monospace" }}
        >
          - HAL 3000
        </p>
      </div>
      
      {/* Instruction */}
      <p className="absolute bottom-8 text-gray-700 font-mono text-xs">
        Touchez pour continuer
      </p>
    </div>
  );
};

// Déclencher HAL par clic (depuis la sidebar)
export const triggerHALClick = () => {
  window.dispatchEvent(new CustomEvent('hal-eye-click'));
};

// Déclencher HAL aléatoirement (depuis Admin)
export const triggerHALEye = () => {
  window.dispatchEvent(new CustomEvent('hal-eye-trigger'));
};

export default HALEyeFullscreen;
